import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, Save, Key } from 'lucide-react';
import { CountryCombobox } from '@/components/CountryCombobox';
import { DEFAULT_COUNTRY } from '@/lib/countries';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

export default function MyProfilePage() {
  const [user, setUser] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editingProfile, setEditingProfile] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    club_id: '',
    contact_phone: ''
  });
  const [roleData, setRoleData] = useState({
    role_type: 'Club',
    role_description: ''
  });
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  const roleTypes = ['Club', 'Regional Committee', 'Area/State Committee', 'National Body', 'International Body', 'WorldSkate'];
  
  const fetchData = async () => {
    try {
      const [userRes, clubsRes, rolesRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/clubs`, { withCredentials: true }),
        axios.get(`${API}/profile/roles`, { withCredentials: true })
      ]);
      setUser(userRes.data);
      const clubTypeOrgs = clubsRes.data.filter(c => c.organisation_type === 'Club');
      setClubs(clubTypeOrgs);
      setRoles(rolesRes.data);
      setProfileData({
        first_name: userRes.data.first_name || '',
        last_name: userRes.data.last_name || '',
        email: userRes.data.email || '',
        club_id: userRes.data.club_id || '',
        contact_phone: userRes.data.contact_phone || '',
        country: userRes.data.country || ''
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.patch(`${API}/profile`, profileData, { withCredentials: true });
      setUser(response.data);
      toast.success('Profile updated!');
      setEditingProfile(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };
  
  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await axios.patch(`${API}/profile/roles/${editingRole.role_id}`, roleData, { withCredentials: true });
        toast.success('Role updated!');
      } else {
        await axios.post(`${API}/profile/roles`, roleData, { withCredentials: true });
        toast.success('Role added!');
      }
      setRoleDialogOpen(false);
      setEditingRole(null);
      setRoleData({ role_type: 'Club', role_description: '' });
      fetchData();
    } catch (error) {
      toast.error(editingRole ? 'Failed to update role' : 'Failed to add role');
    }
  };
  
  const handleEditRole = (role) => {
    setEditingRole(role);
    setRoleData({
      role_type: role.role_type,
      role_description: role.role_description
    });
    setRoleDialogOpen(true);
  };
  
  const handleAddRole = () => {
    setEditingRole(null);
    setRoleData({ role_type: 'Club', role_description: '' });
    setRoleDialogOpen(true);
  };
  
  const handleDeleteRoleClick = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };
  
  const handleConfirmDeleteRole = async () => {
    if (!roleToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/profile/roles/${roleToDelete.role_id}`, { withCredentials: true });
      toast.success('Role deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete role');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        null,
        {
          params: {
            current_password: passwordData.current_password,
            new_password: passwordData.new_password
          },
          withCredentials: true
        }
      );
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };
  
  if (!user) return <div>Loading...</div>;
  
  const userClub = clubs.find(c => c.club_id === user.club_id);
  
  return (
    <div className="space-y-6 fade-in max-w-4xl">
      <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
        My Profile
      </h1>
      
      <Card className="p-8">
        <div className="flex items-start gap-6 mb-6">
          <Avatar className="h-24 w-24">
            <AvatarImage src={user.picture} />
            <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
              {(user.first_name || user.name)?.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h2 className="text-2xl font-semibold mb-1">{user.name}</h2>
            <p className="text-muted-foreground mb-2">{user.email}</p>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${user.role === 'super' ? 'bg-primary/10 text-primary' : 'bg-secondary/10 text-secondary-foreground'}`}>
              {user.role === 'super' ? 'Super User' : 'Standard User'}
            </span>
          </div>
          {!editingProfile && (
            <div className="flex gap-2">
              <Button onClick={() => setEditingProfile(true)} data-testid="edit-profile-button">
                <Edit2 className="mr-2 h-4 w-4" />
                Edit Profile
              </Button>
              <Button variant="outline" onClick={() => setPasswordDialogOpen(true)} data-testid="change-password-button">
                <Key className="mr-2 h-4 w-4" />
                Change Password
              </Button>
            </div>
          )}
        </div>
        
        {editingProfile ? (
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input 
                  value={profileData.first_name} 
                  onChange={(e) => setProfileData({...profileData, first_name: e.target.value})} 
                  required 
                  data-testid="profile-firstname-input"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input 
                  value={profileData.last_name} 
                  onChange={(e) => setProfileData({...profileData, last_name: e.target.value})} 
                  required 
                  data-testid="profile-lastname-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Email (Login Username) *</Label>
              <Input 
                type="email"
                value={profileData.email} 
                onChange={(e) => setProfileData({...profileData, email: e.target.value})} 
                required 
                data-testid="profile-email-input"
              />
              <p className="text-xs text-muted-foreground mt-1">This is your login username. Must be unique.</p>
            </div>
            
            <div>
              <Label>Club - Full Membership</Label>
              <select 
                className="w-full border rounded-lg px-3 py-2" 
                value={profileData.club_id} 
                onChange={(e) => setProfileData({...profileData, club_id: e.target.value})}
                data-testid="profile-club-select"
              >
                <option value="">No Club</option>
                {clubs.map(club => <option key={club.club_id} value={club.club_id}>{club.name}</option>)}
              </select>
            </div>
            
            <div>
              <Label>Contact Phone (NZ Format)</Label>
              <Input 
                value={profileData.contact_phone} 
                onChange={(e) => setProfileData({...profileData, contact_phone: e.target.value})} 
                placeholder="e.g., 021 123 4567"
                data-testid="profile-phone-input"
              />
            </div>

            <div>
              <Label>Country</Label>
              <div className="mt-1">
                <CountryCombobox
                  value={profileData.country || DEFAULT_COUNTRY}
                  onChange={(v) => setProfileData({ ...profileData, country: v })}
                  testId="profile-country"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button type="submit" className="btn-primary" data-testid="profile-save-button">
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditingProfile(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">First Name</p>
                <p className="font-medium">{user.first_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Name</p>
                <p className="font-medium">{user.last_name || '-'}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{user.email}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Club Membership</p>
              <p className="font-medium">{userClub ? userClub.name : 'No club membership'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Contact Phone</p>
              <p className="font-medium">{user.contact_phone || '-'}</p>
            </div>
          </div>
        )}
      </Card>
      
      <Card className="p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Roles in Skating
          </h3>
          <Button onClick={handleAddRole} data-testid="add-role-button">
            <Plus className="mr-2 h-4 w-4" />
            Add Role
          </Button>
        </div>
        
        {roles.length > 0 ? (
          <div className="space-y-3">
            {roles.map(role => (
              <div key={role.role_id} className="flex items-start justify-between p-4 border border-stone-200 rounded-lg hover:bg-stone-50" data-testid={`role-item-${role.role_id}`}>
                <div className="flex-1">
                  <p className="font-medium text-base mb-1">{role.role_type}</p>
                  <p className="text-sm text-muted-foreground">{role.role_description}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEditRole(role)} className="action-icon" data-testid={`edit-role-${role.role_id}`}>
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDeleteRoleClick(role)} className="action-icon" data-testid={`delete-role-${role.role_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-6">No roles added yet. Add your first role!</p>
        )}
      </Card>
      
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Add Role'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRoleSubmit} className="space-y-4">
            <div>
              <Label>Role Type *</Label>
              <select 
                className="w-full border rounded-lg px-3 py-2" 
                value={roleData.role_type} 
                onChange={(e) => setRoleData({...roleData, role_type: e.target.value})}
                data-testid="role-type-select"
                required
              >
                {roleTypes.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            
            <div>
              <Label>Role Description *</Label>
              <Input 
                value={roleData.role_description} 
                onChange={(e) => setRoleData({...roleData, role_description: e.target.value})} 
                placeholder="e.g., Competition Judge, Technical Controller, etc."
                required
                data-testid="role-description-input"
              />
            </div>
            
            <Button type="submit" className="w-full btn-primary" data-testid="role-submit-button">
              {editingRole ? 'Update Role' : 'Add Role'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDeleteRole}
        itemName={roleToDelete?.role_description ? `"${roleToDelete.role_description}"` : 'this role'}
        loading={deleting}
      />
      
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label>Current Password *</Label>
              <Input 
                type="password"
                value={passwordData.current_password} 
                onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})} 
                required
                data-testid="current-password-input"
              />
            </div>
            
            <div>
              <Label>New Password *</Label>
              <Input 
                type="password"
                value={passwordData.new_password} 
                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})} 
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Must be at least 6 characters</p>
            </div>
            
            <div>
              <Label>Confirm New Password *</Label>
              <Input 
                type="password"
                value={passwordData.confirm_password} 
                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})} 
                required
                data-testid="confirm-password-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 btn-primary" disabled={changingPassword} data-testid="submit-password-change">
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
